import { Alert, Box, Button, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type { ApprovalItem } from '../../data/types';
import { dateTime, money } from '../../lib/format';
import { APPROVAL_LABELS } from '../../lib/labels';
import { useRegistry } from '../../store/RegistryStore';
import { useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { ConfirmDialog, useToast } from '../../ui/feedback';
import { AboutThisPage, PageHeader, StatusChip } from '../../ui/primitives';

function linkFor(item: ApprovalItem): string | undefined {
  switch (item.kind) {
    case 'DISTRIBUTION':
      return `/manager/distributions/${item.refId}`;
    case 'CAPITAL_CALL':
      return `/manager/calls/${item.refId}`;
    case 'UNIT_ISSUE':
      return `/manager/applications/${item.refId}`;
    case 'MIGRATION':
      return '/manager/migration';
    case 'ADJUSTMENT':
      return '/manager/audit';
    case 'CAPITAL_RETURN':
      return `/manager/capital-returns/${item.refId}`;
    case 'REDEMPTION_RUN':
      return '/manager/redemptions';
    case 'CALL_DEFAULT':
      return `/manager/calls/${item.refId.split('|')[0]}`;
    case 'ISSUE_RUN':
      return '/manager/offers';
    case 'INVESTOR_REPORT':
      return '/manager/communications';
    case 'OFFER_PUBLISH':
      return `/manager/setup/${item.refId}?step=review`;
    default:
      return '/manager/changes';
  }
}

function ApprovalCard({
  item,
  onDecide,
}: {
  item: ApprovalItem;
  onDecide: (approve: boolean) => void;
}) {
  const scope = useTenantScope();
  const own = item.requestedBy === scope.user.name;
  const canApprove = scope.user.role === 'APPROVER' || scope.user.role === 'ADMIN';
  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}
          >
            {APPROVAL_LABELS[item.kind]} · requested by {item.requestedBy} ·{' '}
            {dateTime(item.requestedAt)}
          </Typography>
          <Typography variant="h6" sx={{ mt: 0.5 }}>
            {item.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {item.summary}
          </Typography>
          {item.amount !== undefined && (
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 700 }}>
              {money(item.amount)}
            </Typography>
          )}
          {item.changes && (
            <Box
              component="table"
              sx={{
                mt: 1.5,
                borderCollapse: 'collapse',
                fontSize: '0.85rem',
                '& td': { pr: 3, py: 0.25 },
              }}
            >
              <tbody>
                {item.changes.map((change) => (
                  <tr key={change.field}>
                    <td>
                      <strong>{change.field}</strong>
                    </td>
                    <td style={{ textDecoration: 'line-through', color: '#94A3B8' }}>
                      {change.from}
                    </td>
                    <td>→ {change.to}</td>
                  </tr>
                ))}
              </tbody>
            </Box>
          )}
        </Box>
        <Stack spacing={1} alignItems={{ md: 'flex-end' }} sx={{ flexShrink: 0 }}>
          <Stack direction="row" spacing={1}>
            {linkFor(item) && (
              <Button component={RouterLink} to={linkFor(item)!} size="small">
                View detail
              </Button>
            )}
            <Button
              size="small"
              color="error"
              variant="outlined"
              disabled={own || !canApprove}
              onClick={() => onDecide(false)}
            >
              Reject
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={own || !canApprove}
              onClick={() => onDecide(true)}
            >
              Approve
            </Button>
          </Stack>
          {own && (
            <Typography variant="caption" color="warning.main">
              You submitted this — another person must approve
            </Typography>
          )}
          {!own && !canApprove && (
            <Typography variant="caption" color="text.secondary">
              Your role cannot approve. Switch to Priya (Approver).
            </Typography>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function ApprovalsPage() {
  const scope = useTenantScope();
  const { dispatch } = useRegistry();
  const toast = useToast();
  const [tab, setTab] = useState(0);
  const [deciding, setDeciding] = useState<{ item: ApprovalItem; approve: boolean } | null>(null);
  const pending = scope.approvals.filter((item) => item.status === 'PENDING');
  const history = scope.approvals.filter((item) => item.status !== 'PENDING');
  return (
    <>
      <PageHeader
        title="Approvals"
        subtitle="Maker-checker control over everything that moves money, units or bank details."
      />
      <AboutThisPage>
        <p>
          Every distribution, capital call, unit issue, transfer, redemption, bank detail change and
          migration needs a second person. The person who submitted an item can never approve it,
          and only users with the Approver role can approve. Decisions are written to the audit
          trail with any comment.
        </p>
      </AboutThisPage>
      {scope.user.role !== 'APPROVER' && pending.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You are signed in as {scope.user.name} ({scope.user.role.toLowerCase().replace('_', ' ')}
          ). Use &quot;Signed in as&quot; in the header to switch to Priya Nair (Approver).
        </Alert>
      )}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_event, next) => setTab(next)}>
          <Tab label={`Pending (${pending.length})`} />
          <Tab label={`History (${history.length})`} />
        </Tabs>
      </Box>
      {tab === 0 && (
        <Stack spacing={1.5}>
          {pending.length === 0 && (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">Nothing waiting for approval.</Typography>
            </Paper>
          )}
          {pending.map((item) => (
            <ApprovalCard
              key={item.id}
              item={item}
              onDecide={(approve) => setDeciding({ item, approve })}
            />
          ))}
        </Stack>
      )}
      {tab === 1 && (
        <Paper variant="outlined">
          <DataTable
            rows={history}
            rowKey={(row) => row.id}
            dense
            defaultSort={{ key: 'decided', direction: 'desc' }}
            columns={[
              { key: 'kind', label: 'Type', render: (row) => APPROVAL_LABELS[row.kind] },
              { key: 'title', label: 'Item', render: (row) => row.title },
              {
                key: 'by',
                label: 'Requested / decided',
                render: (row) => `${row.requestedBy} / ${row.decidedBy ?? '—'}`,
              },
              {
                key: 'decided',
                label: 'Decided',
                sortValue: (row) => row.decidedAt ?? '',
                render: (row) => dateTime(row.decidedAt),
              },
              {
                key: 'comment',
                label: 'Comment',
                hideBelow: 'md',
                render: (row) => row.comment ?? '—',
              },
              {
                key: 'status',
                label: 'Outcome',
                render: (row) => <StatusChip status={row.status} />,
              },
            ]}
          />
        </Paper>
      )}
      <ConfirmDialog
        open={Boolean(deciding)}
        title={
          deciding?.approve
            ? `Approve: ${deciding.item.title}`
            : `Reject: ${deciding?.item.title ?? ''}`
        }
        body={
          deciding?.approve
            ? 'Approving applies the change immediately (ledger, notices, bank file as relevant).'
            : 'The item returns to the maker with your comment.'
        }
        confirmLabel={deciding?.approve ? 'Approve' : 'Reject'}
        danger={!deciding?.approve}
        requireComment={!deciding?.approve}
        commentLabel={deciding?.approve ? 'Comment (optional)' : 'Reason'}
        onClose={() => setDeciding(null)}
        onConfirm={(comment) => {
          if (!deciding) return;
          dispatch({
            type: 'APPROVAL_DECIDE',
            id: deciding.item.id,
            approve: deciding.approve,
            comment: comment || undefined,
          });
          toast(
            deciding.approve ? 'Approved and applied' : 'Rejected and returned',
            deciding.approve ? 'success' : 'warning',
          );
          setDeciding(null);
        }}
      />
    </>
  );
}
